import React, { useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";

import { formatBytes } from "utils/format";
import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";

import styles from "./style.module.scss";

import { useDateRange } from "providers/WithDateRange";
import { Data, InteractionPoint, defined, xValue, yValue } from "ui/Graph/util";

import GraphSection from "ui/Graph/GraphSection";
import { AreaSeries, LineSeries, ScatterSeries } from "ui/Graph/Series";
import DateRangeGraph from "ui/Graph/DateRangeGraph";
import { useRoutes } from "utils/routes";
import { useQueryDetailsGraphQuery } from "./gql/Query.generated";

type Props = {
  databaseId: string;
  queryId: string;
  trackIoTiming: boolean;
};

const Graph: React.FunctionComponent<Props> = ({
  databaseId,
  queryId,
  trackIoTiming,
}) => {
  const { databaseQueryExplain, serverConfigSetting } = useRoutes();
  const { from: newStartTs, to: newEndTs } = useDateRange();

  const { data, loading, error } = useQueryDetailsGraphQuery({
    variables: {
      databaseId,
      queryId,
      startTs: newStartTs.unix(),
      endTs: newEndTs.unix(),
    },
  });

  const { humanId, integratedExplain } = data?.getServerDetails || {};
  const { result, explains } = useMemo(() => {
    const stats = data?.getQueryDetailStats as unknown as Data;
    if (!stats) {
      return { result: undefined, explains: undefined };
    }
    let result = {
      ...stats,
      avgBlksHitPct: stats.avgBlksHit.map((d, i) => {
        const xVal = xValue(d);
        const value = yValue(d);
        if (value == null) {
          return [xVal, null];
        }
        const avgBlksTot = value + yValue(stats.avgBlksRead[i]);
        return [xVal, (value / avgBlksTot) * 100];
      }),
      avgBlksReadPct: stats.avgBlksRead.map((d, i) => {
        const xVal = xValue(d);
        const value = yValue(d);
        if (value == null) {
          return [xVal, null];
        }
        const avgBlksTot = value + yValue(stats.avgBlksHit[i]);
        return [xVal, (value / avgBlksTot) * 100];
      }),
    } as Data;

    if (trackIoTiming) {
      result = {
        ...result,
        cpuTimePct: stats.avgTime.map((d, i) => {
          const xVal = xValue(d);
          const timeTot = yValue(d);
          if (timeTot == null) {
            return [xVal, null];
          }
          const value = Math.max(0, timeTot - yValue(stats.avgIoTime[i]));
          return [xVal, (value / timeTot) * 100];
        }),
        ioTimePct: stats.avgIoTime.map((d, i) => {
          const xVal = xValue(d);
          const timeTot = yValue(stats.avgTime[i]);
          if (timeTot == null) {
            return [xVal, null];
          }
          const value = Math.min(yValue(d), timeTot);
          return [xVal, (value / timeTot) * 100];
        }),
      } as Data;
    }

    let explains = data?.getQueryExplainsForGraph;

    if (explains) {
      // remove explains that happened when no other chart data is available
      explains = explains.filter((e) =>
        defined(result.calls.find((c) => c[0] === e.time)),
      );
      result.explains = explains.map((e) => [e.time, e.querySample.runtimeMs]);
    }
    return { result, explains };
  }, [data, trackIoTiming]);
  const noData = !!result && result.noData != null;

  const navigate = useNavigate();
  const onClick = (point: InteractionPoint) => {
    if (!point) {
      return;
    }
    const d = point.nearby.find((d) => d.series === "explains");
    if (!d) {
      return;
    }
    const id = explains[d.index].humanId;
    navigate(databaseQueryExplain(databaseId, queryId, id));
  };

  return (
    <>
      <Panel title="Avg Time & Calls" key="runtime">
        <GraphSection noData={noData} loading={loading} error={error}>
          <DateRangeGraph
            data={result}
            onClick={onClick}
            axes={{
              left: {
                format: "duration ms",
                tipFormat: (y: number): string => y.toFixed(1) + " ms",
              },
              right: {
                format: "count",
                tipFormat: (y: number): string => y.toFixed(1) + "/min",
              },
            }}
            series={[
              { key: "avgTime", color: "yellowGreen", label: "Avg Total Time" },
              { key: "avgIoTime", color: "turquoise", label: "Avg I/O Time" },
              {
                key: "calls",
                color: "orange",
                label: "Calls",
                yAxis: "right",
                className: styles.newCalls,
              },
              {
                key: "explains",
                type: ScatterSeries,
                label: `EXPLAIN Plan Samples (${
                  integratedExplain ? explains.length : "n/a"
                })`,
                tipLabel: "EXPLAIN",
                color: "violet",
              },
            ]}
          />
        </GraphSection>
      </Panel>
      <Panel title="Cache Hit Ratio" key="blks">
        <GraphSection noData={noData} loading={loading} error={error}>
          <DateRangeGraph
            data={result}
            axes={{
              left: {
                format: "pct",
              },
              right: {
                format: formatBytes,
              },
            }}
            series={[
              {
                key: "avgBlksHitPct",
                type: AreaSeries,
                color: "lime",
                label: "% From Buffer Cache",
                tipLabel: "% Cached",
                className: styles.blocksFromCache,
              },
              {
                key: "avgBlksReadPct",
                type: AreaSeries,
                color: "babyBlue",
                label: "% From Disk",
                tipLabel: "% Disk",
                className: styles.blocksFromDisk,
              },
              {
                key: "bytesLoaded",
                type: LineSeries,
                color: "grey",
                yAxis: "right",
                label: "Bytes Read From Disk (Per Call)",
                tipLabel: "Read From Disk",
              },
            ]}
          />
        </GraphSection>
      </Panel>
      <Panel
        title="CPU vs I/O Time"
        key="cpu_vs_io"
        secondaryTitle={
          <span>
            <strong>Hint:</strong> I/O time is overcounted for parallel query
            plans.
          </span>
        }
      >
        {trackIoTiming && (
          <GraphSection
            noData={noData}
            loading={loading}
            error={error}
            className={styles.trackIoTimingSection}
          >
            <DateRangeGraph
              data={result}
              axes={{
                left: {
                  format: "pct",
                },
              }}
              series={[
                {
                  key: "cpuTimePct",
                  className: styles.cpuTime,
                  type: AreaSeries,
                  color: "lightYellow",
                  label: "% CPU Time",
                },
                {
                  key: "ioTimePct",
                  className: styles.ioTime,
                  type: AreaSeries,
                  color: "babyBlue",
                  label: "% I/O Time",
                },
              ]}
            />
          </GraphSection>
        )}
        {!trackIoTiming && (
          <PanelSection>
            <p>
              <strong>Error:</strong> You don't have{" "}
              <strong>
                <Link to={serverConfigSetting(humanId, "track_io_timing")}>
                  track_io_timing
                </Link>
              </strong>{" "}
              enabled.
            </p>
            <p>
              Enable this setting in your PostgreSQL config to see a breakdown
              of CPU vs I/O time for your queries.
            </p>
          </PanelSection>
        )}
      </Panel>
    </>
  );
};

export default Graph;
